import { pageTitle } from '@palaccount/config';
import type { EChartsOption } from 'echarts';
import { Info } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Chart } from '@/components/chart';
import { QuoteStatusChip } from '@/components/status-chip';
import { TableCell } from '@/components/ui/table';
import { useSessionFilter } from '@/lib/dashboard-filters';
import { useBaseCurrency, useCompanyToday } from '@/lib/session';

import { useSalesWidgets } from './api';
import { Board } from './board';
import { SALES_CATALOG } from './catalog';
import { HomeTabs } from './home-tabs';
import { ChangePill, EmptyLine, KpiValue, RankedBars, WidgetTable, overdueTone } from './widgets';

/** DSH-02 §5a's proposed preset set. */
const RANGES = ['today', 'thisMonth', 'thisQuarter', 'thisYear'] as const;
type RangeKey = (typeof RANGES)[number];

/**
 * The window, anchored on the COMPANY's today rather than the browser's UTC
 * date (N-16 / EC-19-65). The same fault as DSH-10's `presetDates`: in a UTC+3
 * tenant the two are a different DATE between local midnight and 03:00, so
 * "Today" and "This month" ended yesterday while today's invoices were stamped
 * today — the figure was simply smaller than the truth, with nothing on screen
 * to say so.
 */
function rangeDates(range: RangeKey, today: string): { from: string; to: string } {
  const to = today;
  const anchor = new Date(`${today}T00:00:00Z`);
  const y = anchor.getUTCFullYear();
  if (range === 'today') return { from: to, to };
  if (range === 'thisMonth') return { from: `${to.slice(0, 8)}01`, to };
  if (range === 'thisQuarter') {
    const q = Math.floor(anchor.getUTCMonth() / 3) * 3;
    return { from: new Date(Date.UTC(y, q, 1)).toISOString().slice(0, 10), to };
  }
  return { from: `${y}-01-01`, to };
}

/** DSH-02 — the Sales Dashboard on the shared board engine. */
export function SalesDashboardPage() {
  /**
   * GEN-107: what these figures are IN, from the session rather than a literal.
   *
   * THE SENTENCE THAT USED TO STAND HERE said "nothing here converts, so
   * GEN-116 keeps the disclosure off the screen." It was false, and it was
   * false in the direction that hides a defect: `outstandingReceivables` read
   * `unpaid_base` and HAD converted since the freeze wave, while every other
   * money figure on the board added foreign totals to base ones unchanged. The
   * comment described the screen the author believed in rather than the one on
   * disk, and it is why the half-conversion went a whole wave unnoticed.
   *
   * Now every money figure converts on the server at each document's own
   * frozen rate (BR-DSH-02-01), and whether to SAY so is the server's call
   * too — `conversionNote` is non-null exactly when something converted
   * (GEN-115/GEN-116). The browser renders it and decides nothing.
   */
  const baseCurrency = useBaseCurrency();
  const { t, i18n } = useTranslation();
  /**
   * BR-DSH-00-16 — the range choice persists for the browser session and
   * resets to the delivered default (`thisMonth`, §5a) on the next sign-in.
   * The accept function is the select's own option list, so a stored value
   * the screen no longer offers cannot reach `rangeDates`.
   */
  const [range, setRange] = useSessionFilter<RangeKey>(
    'sales.range',
    'thisMonth',
    (value): value is RangeKey => typeof value === 'string' && (RANGES as readonly string[]).includes(value),
  );
  const today = useCompanyToday();
  const dates = useMemo(() => rangeDates(range, today), [range, today]);
  const { data, isError, refetch, dataUpdatedAt } = useSalesWidgets(dates);

  useEffect(() => {
    document.title = pageTitle(t('dash.tabSales'));
  }, [t]);

  const trendOption = useMemo<EChartsOption>(() => {
    const buckets = data?.trend.buckets ?? [];
    return {
      grid: { left: 8, right: 8, top: 8, bottom: 24, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: buckets.map((b) => b.label) },
      yAxis: { type: 'value' },
      series: [{ type: 'bar', data: buckets.map((b) => Number(b.value)), itemStyle: { color: '#2563eb' } }],
    };
  }, [data?.trend.buckets]);

  const stampTime = new Date(dataUpdatedAt || Date.now()).toLocaleTimeString(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const renderWidget = (id: string) => {
    /**
     * ERR-DSH-02-06 / ERR-DSH-02-08 — "Lost the connection mid-load: `This
     * widget could not load. Click refresh to try again.` INSIDE THE AFFECTED
     * CARD ONLY."
     *
     * The line here used to be `if (!data) return <skeleton>`, and a failed
     * query never sets `data`. So a board that could not load did not report
     * an error — it SHIMMERED FOREVER, and a shimmer is the one state a reader
     * waits out rather than acts on. The rule's own remedy ("Click Refresh")
     * was the one thing the screen never suggested.
     *
     * `isError` before `!data` is the whole fix, and the order matters: an
     * errored query is also a query with no data, so the loading arm would go
     * on swallowing it if it came first. The card, not the page, carries the
     * message — §14's "inside the affected card only" — which the shared
     * <Board> gives for free by calling this per widget.
     */
    if (isError) return <EmptyLine text={t('dash.widgetFailed')} />;
    if (!data) return <div className="h-16 animate-pulse rounded bg-muted" aria-busy />;
    switch (id) {
      case 'totalSales':
        return (
          <>
            <KpiValue value={data.totalSales.value} currency={baseCurrency} />
            <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
              <ChangePill changePct={data.totalSales.changePct} />
              {t('dash.vsPrevious')}
            </p>
          </>
        );
      case 'totalToday':
        return (
          <>
            <KpiValue value={data.totalToday} currency={baseCurrency} />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.soFarToday')}</p>
          </>
        );
      case 'openQuotations':
        return (
          <>
            <KpiValue value={String(data.openQuotations.count)} />
            <p className="mt-1 text-xs text-muted-foreground">
              <Amount value={data.openQuotations.total} currency={baseCurrency} className="text-xs" />
            </p>
          </>
        );
      case 'outstandingReceivables':
        return (
          <>
            <KpiValue value={data.outstandingReceivables} currency={baseCurrency} />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.unpaidTotal')}</p>
          </>
        );
      case 'averageOrderValue':
        return (
          <>
            <KpiValue value={data.averageOrderValue} currency={baseCurrency} />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.acrossOrders')}</p>
          </>
        );
      case 'salesTrend':
        return (
          <>
            <p className="mb-1 text-lg font-semibold">
              <Amount value={data.trend.total} currency={baseCurrency} />
            </p>
            {data.trend.buckets.length === 0 ? <EmptyLine text={t('dash.noData')} /> : <Chart option={trendOption} height={160} />}
          </>
        );
      case 'byProduct':
        return <RankedBars rows={data.byProduct} />;
      case 'byCustomer':
        return <RankedBars rows={data.byCustomer} />;
      case 'bySalesperson':
        return <RankedBars rows={data.bySalesperson} />;
      case 'byBranch':
        return <EmptyLine text={t('dash.noData')} />;
      case 'overdueInvoices':
        return data.overdueInvoices.length === 0 ? (
          <EmptyLine text={t('dash.noOverdue')} />
        ) : (
          <WidgetTable head={[t('dash.colCustomer'), t('dash.colAmount'), t('dash.colDaysOverdue'), '']}>
            {data.overdueInvoices.map((row) => (
              <tr key={row.invoiceId} className="border-t">
                <TableCell className="px-0">
                  <p className="truncate">{row.customer}</p>
                  <p className="font-mono text-xs text-muted-foreground">
                    <Ltr value={row.invoiceNo} />
                  </p>
                </TableCell>
                <TableCell className="px-0 text-end"><Amount value={row.unpaid} currency={baseCurrency} /></TableCell>
                <TableCell className={`px-0 text-end ${overdueTone(row.daysOverdue)}`}>
                  <Ltr value={`${row.daysOverdue}d`} className="font-mono" />
                </TableCell>
                {/*
                  BR-DSH-02-18: "Clicking a row's open arrow must open that
                  document in read mode, and must not change it." Both halves
                  hold by construction rather than by a flag anyone can forget.
                  A <Link> is a GET; it writes nothing. And the ONLY invoices
                  this table can hold are posted ones — `arOpenSql` selects
                  `status = 'posted'` — while the invoice editor computes
                  `editable = isDraft && canEdit`, so every field it draws for
                  a posted invoice is disabled. There is no path from this
                  arrow to an editable invoice.

                  ERR-DSH-02-05 / ERR-DSH-02-09 — "A row's document cannot be
                  opened for lack of permission" — cannot arise from THIS
                  board. `DashboardsService.sales()` demands `sales.view`
                  before it computes a single figure (BR-DSH-00-17), and
                  `/sales/:invoiceId` requires the same `sales` area through
                  route-permissions, so a reader who can see the arrow can
                  always open what it points at. §6's "hidden for a user
                  without permission on that document" is satisfied by the
                  whole screen being absent for them. Should the two gates ever
                  part — a per-document rule, say — the arrow would need the
                  fence back; the sentence itself is not built, and this sweep
                  reports it rather than claiming it.
                */}
                <TableCell className="px-0 text-end">
                  <Link className="text-primary" to={`/sales/${row.invoiceId}`} aria-label={t('dash.openDocument')}>
                    ›
                  </Link>
                </TableCell>
              </tr>
            ))}
          </WidgetTable>
        );
      case 'recentQuotations':
        return data.recentQuotations.length === 0 ? (
          <EmptyLine text={t('dash.noQuotes')} />
        ) : (
          <WidgetTable head={[t('dash.colCustomer'), t('dash.colAmount'), t('dash.colStatus'), '']}>
            {data.recentQuotations.map((row) => (
              <tr key={row.quotationId} className="border-t">
                <TableCell className="px-0">
                  <p className="truncate">{row.customer}</p>
                  <p className="font-mono text-xs text-muted-foreground">
                    <Ltr value={row.quoteNo} />
                  </p>
                </TableCell>
                <TableCell className="px-0 text-end"><Amount value={row.total} currency={baseCurrency} /></TableCell>
                {/*
                  BR-DSH-02-16 — "A quotation chip must be grey for Draft, blue
                  for Sent, green for Accepted, and amber for Expired." This
                  cell drew bare text with no chip and no colour at all, so the
                  rule had nothing to be right or wrong about.

                  It draws SAL-01's own `QuoteStatusChip` rather than a private
                  map, which is GEN-66 (the document's status chip is the one
                  drawn) and what keeps NFR-14's five meanings identical here
                  and on the quotation screen. That inherits SAL-01 §7's AMBER
                  for Expired, which used to be a DEVIATION from this rule's
                  "red" and is now simply the rule: DECISION 9 (26 Aug 2026)
                  confirmed amber and corrected the dashboard sentence. The
                  argument that earned it is beside the query that feeds this;
                  the short form is NFR-14 — amber means waiting, red means a
                  problem, and an expired quotation is still convertible (R4).

                  §5l field 35: "Leading-aligned, unlike the two money
                  columns." The cell was `text-end` with them.
                */}
                <TableCell className="px-0 text-start">
                  <QuoteStatusChip status={row.status} />
                </TableCell>
                <TableCell className="px-0 text-end">
                  <Link className="text-primary" to={`/sales/quotes/${row.quotationId}`} aria-label={t('dash.openDocument')}>
                    ›
                  </Link>
                </TableCell>
              </tr>
            ))}
          </WidgetTable>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <HomeTabs />
      <h1 className="text-xl font-semibold">{t('dash.tabSales')}</h1>
      <Board
        board="sales"
        catalog={SALES_CATALOG}
        stamp={{ text: t('dash.updatedAt', { time: stampTime }), refresh: () => void refetch() }}
        /**
         * GEN-115 directly above the numbers, GEN-116 hidden when nothing
         * converted, GEN-117 informational grey with the info icon — the same
         * band DSH-04 uses, drawn from the same server decision. The sentence
         * names the BASIS rather than a rate date because there is no single
         * date to name: every document here converts at the rate IT froze.
         */
        disclosure={
          data?.conversionNote == null ? null : (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5" />
              {data.conversionNote}
            </p>
          )
        }
        /**
         * ONE FILTER, WHERE §4 DRAWS FOUR — recorded here because the absence
         * is invisible and four rules depend on it. DSH-02 §4's action bar is
         * `[This month v] [All branches v] [All salespeople v] [All segments v]`
         * and this board offers only the first. Every line below says what is
         * missing and why it is not a wave's to add. ONE of them is now a real
         * citation rather than a queued absence — BR-DSH-02-22, settled by
         * DECISION 30 — and it carries no `rule-coverage:ignore` for exactly
         * that reason; the rest still do.
         *
         * ── 27 AUG: THE OTHER THREE ARE RULED TOO, AND RETIRED ────────────
         *
         * Rulings D41, D42 and D43 settle the three missing filters, each
         * reversibly, and the specification now carries a retirement notice as
         * the entire body of every rule named below. So this block no longer
         * describes three rules waiting on a decision; it describes three
         * decisions taken, and the code that already matched them.
         *
         * **NOTHING BELOW WAS WRONG, AND THAT IS THE POINT.** Every paragraph
         * here was true when written and stayed true, and the rules sat in the
         * coverage queue the whole time regardless — because a
         * `rule-coverage:ignore` mark stops an id being counted as CITED and
         * does nothing whatever to the UNNAMED side. A wave reading this queue
         * would have found a rule it could not build and written the same
         * paragraph again. Only a notice in the document removes it.
         *
         * THE MARKS STAY. A retired rule MAY be cited, so they no longer
         * protect a counter; they protect the claim, because every id below
         * appears inside a sentence explaining why nothing enforces it.
         *
         * BR-DSH-02-02 — the Branch filter, hidden at one    rule-coverage:ignore
         *   branch and listing All branches plus every branch above one. Not
         *   built. It is the same knot as `byBranch` in `catalog.ts`: the
         *   product has no branch dimension, and inventing one would settle
         *   §1.5.3 against §22.5 inside a dropdown.
         *   RULED 27 Aug, D42: RETIRED, reversibly. There is no branch entity
         *   and the branch dimension is deliberately Phase 2, so reading a
         *   warehouse flagged as a branch as a proxy would bake a wrong data
         *   model into a paid module's first screen. The filter returns when
         *   branches exist as entities — which is the same answer this
         *   paragraph reached, now with an authority behind it.
         *
         * BR-DSH-02-22 — "every widget must respect the four global filters".
         *   Three of the four do not exist, so there is nothing for the
         *   widgets to respect. The one that does exist IS respected — with
         *   one measured exception the earlier note glossed: `dates` travels
         *   in the query key and in the request and the KPI, trend and ranked
         *   queries all take `BETWEEN from AND to` (BR-DSH-00-13), but the
         *   server's `recentQuotes` query carries NO date clause at all — the
         *   newest 8, whatever the window says.
         *
         *   THAT EXCEPTION IS NOW DELIBERATE, AND THIS IS ITS AUTHORITY.
         *   Whether it was a breach or the nature of the widget was owner
         *   decision 30's exact question (worklist, or figures?). RULED
         *   26 Aug 2026: "Recent Quotations → worklist; joins BR-DSH-00-26's
         *   named exceptions." A worklist shows CURRENT WORK, so it ignores
         *   the window the same way and for the same reason it ignores the
         *   posted-only rule (it is BR-DSH-00-26's fourth    rule-coverage:ignore
         *   exception, pinned with this ruling in dsh00-posted-only.spec.ts).
         *   A figures widget would have had to take the filter; this is not
         *   one — it draws no total and sums no period.
         *
         *   So the rule no longer STOPS here. It is cited, and the behaviour
         *   is pinned from the outside by `dsh02-worklist-date-filter.e2e`,
         *   which posts a quotation outside the window and asserts the widget
         *   still lists it. Adding a date clause is now the regression.
         *
         *   Still short of full compliance, and honestly: the other three
         *   filters remain unbuilt, and BR-DSH-02-02 and    rule-coverage:ignore
         *   BR-DSH-02-23 below are the rules that own    rule-coverage:ignore
         *   that gap. D30 settled the half that was decidable.
         *
         * BR-DSH-02-19 — the Sales Trend range buttons    rule-coverage:ignore
         *   "must change only that widget". There are no per-widget range
         *   buttons anywhere in the cluster — the same absence    rule-coverage:ignore
         *   BR-DSH-00-14 names for the saved layout, whose    rule-coverage:ignore
         *   `LayoutEntrySchema` is `{id,size,visible,order}`: there is no
         *   per-widget range for §14.1.7 to have saved.
         *   Re-verified 26 Aug and upheld: §5i field 20 defines the buttons
         *   (`7d/30d/90d/YTD`, overriding the global filter for this widget
         *   only) but field 22's bucket sizing is `[NOT IN PROTOTYPE —
         *   decide at build time]`, the override semantics are
         *   BR-DSH-00-14's (a rule queued to a different    rule-coverage:ignore
         *   block), and the proposed buckets collide with BR-DSH-02-20's
         *   pinned "ten buckets drawn" (7d would draw seven). Building the
         *   buttons alone would decide the marker and cross the other
         *   block's rule; the knot is one build, not this one.
         *   RULED 27 Aug, D41: BOTH RULES RETIRED, reversibly — the trend
         *   rule here and the framework rule it defers to. The ruling takes
         *   this paragraph's three reasons as its own: the buttons duplicate
         *   the global filter's reach, they need the saved-layout schema
         *   change, and the bucket collision is unresolved. They return as
         *   Phase-2 polish with the bucket question decided then.
         *
         * BR-DSH-02-23 — a salesperson who may see only    rule-coverage:ignore
         *   their own documents gets the Salesperson filter locked to their
         *   name. Blocked twice over: there is no Salesperson filter, and
         *   there is no "own documents only" scope in the permission model to
         *   detect. Marked `[NOT IN PROTOTYPE — decide at build time]` in the
         *   spec, and BR-DSH-05-16 is the same rule waiting    rule-coverage:ignore
         *   on the same decision for Sales Reports (DSH-05).
         *   Re-verified 26 Aug, both halves: the permission model is the
         *   closed 12×5 grid plus named extensions (permissions.ts — no
         *   own-scope anywhere in it), and while `DashboardsService.sales`
         *   now ACCEPTS a `salespersonId` and threads it into four queries,
         *   no filter UI sends it and nothing could lock it. The groundwork
         *   shortens the eventual build; it decides nothing.
         *   RULED 27 Aug, D43: BOTH RULES RETIRED, reversibly — this one and
         *   the Sales Reports twin. The sales rep stays optional until there
         *   are real tenants (A1), and minting a per-user document scope in
         *   the closed permission grid for two dashboard filters inverts the
         *   cost. They return with the sales-rep dimension, and the
         *   groundwork above is what makes that return short.
         */
        filters={
          <label className="flex items-center gap-2 text-sm">
            <span className="sr-only">{t('dash.filterDateRange')}</span>
            <select
              className="rounded-md border bg-background px-2 py-1.5"
              value={range}
              onChange={(e) => setRange(e.target.value as RangeKey)}
            >
              {RANGES.map((r) => (
                <option key={r} value={r}>
                  {t(`dash.range_${r}`)}
                </option>
              ))}
            </select>
          </label>
        }
        renderWidget={renderWidget}
      />
    </div>
  );
}
